/** biome-ignore-all lint/suspicious/noConsole: Required for logging in development and for Vercel Runtime Logs */
import { SpanKind, SpanStatusCode, trace } from "@opentelemetry/api";
import { track } from "@vercel/analytics/server";

/**
 * Log levels for structured logging
 */

// biome-ignore lint/style/noEnum: Required for log level constants
export enum LogLevel {
  DEBUG = "debug",
  INFO = "info",
  WARN = "warn",
  ERROR = "error",
}

/**
 * Base log entry structure for Vercel Runtime Logs compatibility
 */
type BaseLogEntry = {
  timestamp: string;
  level: LogLevel;
  message: string;
  service: string;
  environment: string;
  traceId?: string;
  spanId?: string;
  [key: string]: unknown;
};

/**
 * Shopify API specific log entry
 */
interface ShopifyLogEntry extends BaseLogEntry {
  shopify: {
    operation: string;
    operationType: "query" | "mutation" | "rest";
    shop: string;
    duration: number;
    status?: number;
    rateLimitRemaining?: number;
    rateLimitMax?: number;
    cost?: number;
    costRemaining?: number;
    errorCode?: string;
    variables?: Record<string, unknown>;
  };
}

/**
 * oRPC API specific log entry
 */
interface ORPCLogEntry extends BaseLogEntry {
  orpc: {
    procedure: string;
    method: string;
    duration: number;
    status: "success" | "error";
    errorCode?: string;
    userId?: string;
    input?: Record<string, unknown>;
  };
}

/**
 * Performance metrics log entry
 */
interface PerformanceLogEntry extends BaseLogEntry {
  performance: {
    operation: string;
    duration: number;
    memoryUsage?: number;
    cpuUsage?: number;
    metadata?: Record<string, unknown>;
  };
}

/**
 * Structured logger class for WillPower Store
 */
class Logger {
  private readonly serviceName: string;
  private readonly environment: string;

  constructor(
    serviceName = "willpower-store",
    environment = process.env.NODE_ENV || "development"
  ) {
    this.serviceName = serviceName;
    this.environment = environment;
  }

  /**
   * Get current trace context for correlation
   */
  private getTraceContext() {
    const activeSpan = trace.getActiveSpan();
    if (activeSpan) {
      const spanContext = activeSpan.spanContext();
      return {
        traceId: spanContext.traceId,
        spanId: spanContext.spanId,
      };
    }
    return {};
  }

  /**
   * Create base log entry with common fields
   */
  private createBaseEntry(level: LogLevel, message: string): BaseLogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      service: this.serviceName,
      environment: this.environment,
      ...this.getTraceContext(),
    };
  }

  /**
   * Log a general message
   */
  log(level: LogLevel, message: string, metadata?: Record<string, unknown>) {
    const entry = {
      ...this.createBaseEntry(level, message),
      ...metadata,
    };

    // Use different output format based on environment
    if (process.env.NODE_ENV === "development") {
      this.logDevelopment(level, message, metadata);
    } else {
      // Use console.log for Vercel Runtime Logs compatibility in production
      console.log(JSON.stringify(entry));
    }
  }

  /**
   * Development-friendly logging format
   */
  private logDevelopment(
    level: LogLevel,
    message: string,
    metadata?: Record<string, unknown>
  ) {
    const timestamp = new Date().toLocaleTimeString();
    const levelColor = this.getLevelColor(level);
    const resetColor = "\x1b[0m";

    // Format the main log line
    let logLine = `${levelColor}[${level.toUpperCase()}]${resetColor} ${timestamp} ${message}`;

    // Add metadata if present
    if (metadata && Object.keys(metadata).length > 0) {
      const filteredMetadata = this.filterSensitiveData(metadata);
      logLine += `\n  ${JSON.stringify(filteredMetadata, null, 2).split("\n").join("\n  ")}`;
    }

    console.log(logLine);
  }

  /**
   * Get ANSI color codes for log levels
   */
  private getLevelColor(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG:
        return "\x1b[36m"; // Cyan
      case LogLevel.INFO:
        return "\x1b[32m"; // Green
      case LogLevel.WARN:
        return "\x1b[33m"; // Yellow
      case LogLevel.ERROR:
        return "\x1b[31m"; // Red
      default:
        return "\x1b[0m"; // Reset
    }
  }

  /**
   * Filter out sensitive data from logs
   */
  private filterSensitiveData(
    data: Record<string, unknown>
  ): Record<string, unknown> {
    const sensitiveKeys = [
      "password",
      "token",
      "secret",
      "key",
      "authorization",
    ];
    const filtered: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(data)) {
      if (
        sensitiveKeys.some((sensitive) => key.toLowerCase().includes(sensitive))
      ) {
        filtered[key] = "[REDACTED]";
      } else if (typeof value === "object" && value !== null) {
        filtered[key] = this.filterSensitiveData(
          value as Record<string, unknown>
        );
      } else {
        filtered[key] = value;
      }
    }

    return filtered;
  }

  /**
   * Log info message
   */
  info(message: string, metadata?: Record<string, unknown>) {
    this.log(LogLevel.INFO, message, metadata);
  }

  /**
   * Log warning message
   */
  warn(message: string, metadata?: Record<string, unknown>) {
    this.log(LogLevel.WARN, message, metadata);
  }

  /**
   * Log error message
   */
  error(message: string, error?: Error, metadata?: Record<string, unknown>) {
    const errorMetadata = error
      ? {
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack,
          },
        }
      : {};

    this.log(LogLevel.ERROR, message, {
      ...errorMetadata,
      ...metadata,
    });
  }

  /**
   * Log debug message (only in development)
   */
  debug(message: string, metadata?: Record<string, unknown>) {
    if (this.environment === "development" || process.env.DEBUG === "true") {
      this.log(LogLevel.DEBUG, message, metadata);
    }
  }

  /**
   * Log Shopify API call
   */
  logShopifyCall(
    entry: Omit<ShopifyLogEntry["shopify"], "shop"> & { shop?: string }
  ) {
    const message = `Shopify ${entry.operationType} operation: ${entry.operation}`;

    if (process.env.NODE_ENV === "development") {
      this.logShopifyDevelopment(entry);
    } else {
      const shopifyEntry: ShopifyLogEntry = {
        ...this.createBaseEntry(LogLevel.INFO, message),
        shopify: {
          ...entry,
          shop: entry.shop || process.env.SHOPIFY_STORE_DOMAIN || "unknown",
        },
      };
      console.log(JSON.stringify(shopifyEntry));
    }
  }

  /**
   * Development-friendly Shopify logging
   */
  private logShopifyDevelopment(
    entry: Omit<ShopifyLogEntry["shopify"], "shop"> & { shop?: string }
  ) {
    const timestamp = new Date().toLocaleTimeString();
    const statusColor =
      entry.status && entry.status >= 400 ? "\x1b[31m" : "\x1b[32m"; // Red for errors, green for success
    const resetColor = "\x1b[0m";
    const shopifyColor = "\x1b[35m"; // Magenta for Shopify

    let logLine = `${shopifyColor}[SHOPIFY]${resetColor} ${timestamp} ${entry.operation}`;

    // Add status and duration
    if (entry.status) {
      logLine += ` ${statusColor}${entry.status}${resetColor}`;
    }
    if (entry.duration) {
      logLine += ` (${entry.duration}ms)`;
    }

    // Add rate limit info if available
    if (
      entry.rateLimitRemaining !== undefined &&
      entry.rateLimitMax !== undefined
    ) {
      const rateLimitPercent = Math.round(
        (entry.rateLimitRemaining / entry.rateLimitMax) * 100
      );
      const rateLimitColor = rateLimitPercent < 20 ? "\x1b[33m" : "\x1b[36m"; // Yellow if low, cyan otherwise
      logLine += ` ${rateLimitColor}[${entry.rateLimitRemaining}/${entry.rateLimitMax}]${resetColor}`;
    }

    // Add cost info if available
    if (entry.cost !== undefined) {
      logLine += ` \x1b[36m[Cost: ${entry.cost}]\x1b[0m`;
    }

    console.log(logLine);

    // Log error details if present
    if (entry.errorCode) {
      console.log(`  \x1b[31mError: ${entry.errorCode}\x1b[0m`);
    }

    // Log variables in debug mode
    if (process.env.DEBUG === "true" && entry.variables) {
      console.log(
        `  \x1b[90mVariables: ${JSON.stringify(entry.variables)}\x1b[0m`
      );
    }
  }

  /**
   * Log oRPC procedure call
   */
  logORPCCall(entry: ORPCLogEntry["orpc"]) {
    const message = `oRPC ${entry.method} procedure: ${entry.procedure}`;
    const level = entry.status === "error" ? LogLevel.ERROR : LogLevel.INFO;

    if (process.env.NODE_ENV === "development") {
      this.logORPCDevelopment(entry);
    } else {
      const orpcEntry: ORPCLogEntry = {
        ...this.createBaseEntry(level, message),
        orpc: entry,
      };
      console.log(JSON.stringify(orpcEntry));
    }
  }

  /**
   * Development-friendly oRPC logging
   */
  private logORPCDevelopment(entry: ORPCLogEntry["orpc"]) {
    const timestamp = new Date().toLocaleTimeString();
    const statusColor = entry.status === "error" ? "\x1b[31m" : "\x1b[32m"; // Red for errors, green for success
    const resetColor = "\x1b[0m";
    const orpcColor = "\x1b[34m"; // Blue for oRPC

    let logLine = `${orpcColor}[oRPC]${resetColor} ${timestamp} ${entry.method} ${entry.procedure}`;

    // Add status and duration
    logLine += ` ${statusColor}${entry.status}${resetColor}`;
    if (entry.duration) {
      logLine += ` (${entry.duration}ms)`;
    }

    console.log(logLine);

    // Log input in debug mode
    if (process.env.DEBUG === "true" && entry.input) {
      console.log(`  \x1b[90mInput: ${JSON.stringify(entry.input)}\x1b[0m`);
    }

    // Log error code if present
    if (entry.errorCode) {
      console.log(`  \x1b[31mError Code: ${entry.errorCode}\x1b[0m`);
    }
  }

  /**
   * Log performance metrics
   */
  logPerformance(entry: PerformanceLogEntry["performance"]) {
    const performanceEntry: PerformanceLogEntry = {
      ...this.createBaseEntry(LogLevel.INFO, `Performance: ${entry.operation}`),
      performance: entry,
    };

    console.log(JSON.stringify(performanceEntry));
  }

  /**
   * Create a timed operation wrapper with automatic logging
   */
  async withTiming<T>(
    operation: string,
    fn: () => Promise<T>,
    metadata?: Record<string, unknown>
  ): Promise<T> {
    const startTime = Date.now();
    const tracer = trace.getTracer("willpower-store");

    return tracer.startActiveSpan(
      operation,
      { kind: SpanKind.INTERNAL },
      async (span) => {
        try {
          const result = await fn();
          const duration = Date.now() - startTime;

          span.setStatus({ code: SpanStatusCode.OK });
          span.setAttributes({
            "operation.name": operation,
            "operation.duration": duration,
            ...metadata,
          });

          this.logPerformance({
            operation,
            duration,
            metadata,
          });

          return result;
        } catch (error) {
          const duration = Date.now() - startTime;

          span.setStatus({
            code: SpanStatusCode.ERROR,
            message: error instanceof Error ? error.message : "Unknown error",
          });

          span.setAttributes({
            "operation.name": operation,
            "operation.duration": duration,
            "operation.error": true,
            ...metadata,
          });

          this.error(
            `Operation failed: ${operation}`,
            error instanceof Error ? error : new Error(String(error)),
            {
              operation,
              duration,
              ...metadata,
            }
          );

          throw error;
        } finally {
          span.end();
        }
      }
    );
  }

  /**
   * Track server-side analytics events with Vercel Analytics
   */
  async trackServerEvent(
    eventName: string,
    properties?: Record<string, string | number | boolean>
  ): Promise<void> {
    try {
      // Track with Vercel Analytics
      await track(eventName, properties);

      // Log the event for debugging
      this.info(`Analytics event tracked: ${eventName}`, {
        event: eventName,
        properties,
      });
    } catch (error) {
      this.error("Failed to track analytics event", error as Error, {
        event: eventName,
        properties,
      });
    }
  }

  /**
   * Track Shopify API usage for analytics
   */
  async trackShopifyUsage(
    operation: string,
    success: boolean,
    duration: number,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    try {
      await this.trackServerEvent("Shopify API Call", {
        operation,
        success,
        duration_ms: duration,
        ...(metadata &&
          Object.fromEntries(
            Object.entries(metadata)
              .filter(
                ([, value]) =>
                  typeof value === "string" ||
                  typeof value === "number" ||
                  typeof value === "boolean"
              )
              .map(([key, value]) => [key, value as string | number | boolean])
          )),
      });
    } catch (error) {
      this.error("Failed to track Shopify usage", error as Error, {
        operation,
        success,
        duration,
      });
    }
  }

  /**
   * Track e-commerce events from server-side
   */
  async trackEcommerceEvent(
    event:
      | "order_created"
      | "payment_processed"
      | "inventory_updated"
      | "customer_registered",
    data: Record<string, string | number | boolean>
  ): Promise<void> {
    try {
      await this.trackServerEvent(
        `Ecommerce ${event.replace(/_/g, " ")}`,
        data
      );
    } catch (error) {
      this.error("Failed to track e-commerce event", error as Error, {
        event,
        data,
      });
    }
  }
}

// Export singleton logger instance
export const logger = new Logger();

// Export types for external use
export type { ShopifyLogEntry, ORPCLogEntry, PerformanceLogEntry };
